// src/features/companies/components/CompanyManagement.jsx
import React, { useState } from 'react';
import { companiesApi } from '../../hooks/companiesApi';
import CompanyList from './components/CompanyList';
import CompanyForm from './components/CompanyForm';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import ErrorMessage from '../../components/UI/ErrorMessage';
import { PlusIcon, BuildingOfficeIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const CompanyManagement = () => {
    const { 
        companies, 
        loading, 
        error, 
        createCompany, 
        updateCompany, 
        deleteCompany,
        fetchCompanies 
    } = companiesApi();

    const [showCompanyForm, setShowCompanyForm] = useState(false);
    const [editingCompany, setEditingCompany] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const handleCreateCompany = async (companyData) => {
        try {
            await createCompany(companyData);
            setShowCompanyForm(false);
        } catch (error) {
            throw error;
        }
    };

    const handleEditCompany = (company) => {
        setEditingCompany(company);
        setShowCompanyForm(true);
    };

    const handleUpdateCompany = async (companyData) => {
        try {
            await updateCompany(editingCompany.nit, companyData);
            setEditingCompany(null);
            setShowCompanyForm(false);
        } catch (error) {
            throw error;
        }
    };

    const handleCancelForm = () => {
        setShowCompanyForm(false);
        setEditingCompany(null);
    };

    // Filtrar empresas basado en el término de búsqueda
    const filteredCompanies = companies.filter(company =>
        company.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.nit.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.contacto.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return <LoadingSpinner />;
    if (error) return <ErrorMessage error={error} onRetry={fetchCompanies} />;

    return (
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
            {/* Header con botón de acción - Mejorado para móvil */}
            <div className="mb-6">
                <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center">
                        <BuildingOfficeIcon className="h-8 w-8 text-blue-600 mr-3" />
                        <div>
                            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Gestión de Empresas</h1>
                            <p className="text-sm sm:text-base text-gray-600">
                                {filteredCompanies.length} empresa{filteredCompanies.length !== 1 ? 's' : ''} en el sistema
                            </p>
                        </div>
                    </div>
                    
                    <button
                        onClick={() => setShowCompanyForm(true)}
                        className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 w-full sm:w-auto"
                    >
                        <PlusIcon className="h-4 w-4 mr-2" />
                        Nueva Empresa
                    </button>
                </div>

                {/* Barra de búsqueda */}
                <div className="mt-4 max-w-md">
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                            type="text"
                            placeholder="Buscar empresas..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                    </div>
                </div>
            </div>

            {/* Modal para crear/editar empresa - Mejorado para móvil */}
            {showCompanyForm && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-4 sm:top-20 mx-auto p-3 sm:p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
                        <div className="mt-3">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-medium text-gray-900">
                                    {editingCompany ? 'Editar Empresa' : 'Crear Nueva Empresa'}
                                </h3>
                                <button
                                    onClick={handleCancelForm}
                                    className="text-gray-400 hover:text-gray-600 text-2xl"
                                >
                                ×
                                </button>
                            </div>
                        
                            <CompanyForm
                                company={editingCompany}
                                onSubmit={editingCompany ? handleUpdateCompany : handleCreateCompany}
                                onCancel={handleCancelForm}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Lista de empresas */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <CompanyList 
                    companies={filteredCompanies}
                    onEdit={handleEditCompany}
                    onDelete={deleteCompany}
                />
            </div>

            {/* Mensaje cuando no hay resultados de búsqueda */}
            {filteredCompanies.length === 0 && companies.length > 0 && (
                <div className="text-center py-8">
                    <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No se encontraron empresas</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        No hay empresas que coincidan con "{searchTerm}".
                    </p>
                </div>
            )}
        </div>
    );
};

export default CompanyManagement;